import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";
import { Back } from "../../icons/Back";
import {
  getStatusColor,
  getStatusTextColor,
  recruitStatusMeta,
} from "../Dropdowns/StatusDropdown";
import { Select } from "../../icons/Select";
import Checkbox from "../Buttons/Checkbox";
import { Exclamation } from "../../icons/Exclamation";

const STORAGE_KEY = "statusSortOrder";

const getStatusList = () =>
  Object.entries(recruitStatusMeta).map(([code, meta]) => ({
    code,
    ...meta,
  }));

const StatusSortDialog = ({ isOpen, onClose = () => {}, dialogRef }) => {
  // Load order from localStorage or default
  const [statuses, setStatuses] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const defaultList = getStatusList();
    if (saved) {
      try {
        const savedOrder = JSON.parse(saved);
        // Map saved order to full status objects, fallback to default if missing
        return savedOrder
          .map((code) => defaultList.find((s) => s.code === code))
          .filter(Boolean)
          .concat(defaultList.filter((s) => !savedOrder.includes(s.code)));
      } catch {
        return defaultList;
      }
    }
    return defaultList;
  });

  // Track checked statuses by code
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(getStatusList().map((s) => [s.code, true]))
  );
  const dragItem = useRef();
  const dragOverItem = useRef();

  useEffect(() => {
    if (!isOpen) return;
    // When dialog opens, reload order from storage (optional: comment out if you want to keep in-memory order)
    const saved = localStorage.getItem(STORAGE_KEY);
    const defaultList = getStatusList();
    if (saved) {
      try {
        const savedOrder = JSON.parse(saved);
        setStatuses(
          savedOrder
            .map((code) => defaultList.find((s) => s.code === code))
            .filter(Boolean)
            .concat(defaultList.filter((s) => !savedOrder.includes(s.code)))
        );
      } catch {
        setStatuses(defaultList);
      }
    } else {
      setStatuses(defaultList);
    }
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof window === "undefined") return null;

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    const list = [...statuses];
    const draggedItem = list[dragItem.current];
    list.splice(dragItem.current, 1);
    list.splice(dragOverItem.current, 0, draggedItem);
    setStatuses(list);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleCheckboxChange = (code) => {
    setChecked((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const handleSave = () => {
    // Save order to localStorage
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(statuses.map((s) => s.code))
    );
    onClose();
  };

  return ReactDOM.createPortal(
    <div
      ref={dialogRef}
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
    >
      <div className="bg-white w-[420px] rounded-md flex flex-col relative">
        {/* Header */}
        <div className="relative flex items-center bg-[#0832DE] rounded-t-md py-7 px-6">
          <div className="flex flex-col gap-y-1 mx-auto items-center">
            <h2 className="text-white text-lg font-bold">
              Сортировка диапазона
            </h2>
            <h2 className="text-white text-lg font-bold">“Status”</h2>
          </div>
          <button
            type="button"
            className="absolute right-6 top-1/2 -translate-y-1/2 p-2 text-white hover:text-gray-200 flex items-center justify-center bg-[#0832DE] rounded-full"
            style={{ minWidth: 40, minHeight: 40 }}
            onClick={onClose}
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-row items-center gap-x-3 text-gray-500 mt-6 mx-6">
          <div className="px-1.5">
            <Exclamation className="w-6 h-6 flex-shrink-0 self-center" />
          </div>
          <span className="text-xs flex-1 leading-tight">
            Перетаскивайте элементы что бы изменить порядок отображения статусов
            в колонке
          </span>
        </div>

        {/* Draggable Statuses */}
        <div className="flex-1 flex flex-col px-6 py-4 overflow-auto">
          <div className="space-y-1 p-5 h-[380px] bg-[#F5F6F7] rounded-lg overflow-auto">
            {statuses.map((status, idx) => (
              <div
                key={status.code}
                className="flex items-center bg-[#F5F6F7] rounded cursor-move"
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                style={{ opacity: dragItem.current === idx ? 0.5 : 1 }}
              >
                <span className="text-gray-400">
                  <Select className="w-6 h-6"/>
                </span>
                <div className="flex flex-col bg-white w-full py-1 gap-0.5 px-2.5">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${getStatusTextColor(
                          status.code
                        )} ${getStatusColor(status.code)}`}
                      >
                        {status.code}
                      </span>
                      <div className="flex flex-col">
                        <span className="text-gray-800 font-medium">
                          {status.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {status.description}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Checkbox
                        checked={checked[status.code]}
                        onChange={() => handleCheckboxChange(status.code)}
                        className="stroke-[#0832DE]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between px-6 pb-4 pt-2">
          <button className="bg-[#F5F6F7] rounded-lg p-2" onClick={onClose}>
            <Back className="w-6 h-6" />
          </button>
          <button
            type="button"
            className="px-8 text-white text-sm py-3 rounded-lg mr-2"
            style={{ backgroundColor: "#0832DE" }}
            onClick={handleSave}
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StatusSortDialog;
